import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconButton, Tooltip } from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { Authorize } from '@/components';
import { BUSINESS_ROLE_GROUPS } from '@/components/security/AppRoles';
import Table from '@/components/table/Table';
import { PaginationConfig } from '@/components/table/TablePagination';
import {
  useDeleteCommerceSettings,
  useCommerceSettings,
  useCommerceSettingsPagination,
} from '../hooks';
import { commerceSettingsTableColumns } from '../config';
import { CommerceSettings } from '../models';

interface CommerceSettingsTableProps {
  filters: {
    key?: string;
    value?: string;
  };
  commerceId: string;
}

const CommerceSettingsTable = ({
  filters,
  commerceId,
}: CommerceSettingsTableProps) => {
  const navigate = useNavigate();
  const { currentPage, pageSize, handlePageChange, handlePageSizeChange } =
    useCommerceSettingsPagination();

  const { commerceSettings, isLoading, error, totalItems, totalPages } =
    useCommerceSettings({
      page: currentPage,
      pageSize,
      filters: {
        key: filters.key,
        value: filters.value,
        commerceId: commerceId,
      },
    });

  const {
    handleDeleteCommerceSettings,
    isLoading: isDeletingCommerceSettings,
  } = useDeleteCommerceSettings({
    onSuccess: () => {},
  });

  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleEdit = (commerceSettingsId: string) => {
    navigate(
      `/business/commerce/${commerceId}/settings/${commerceSettingsId}/edit`
    );
  };

  const handleDelete = async (commerceSettingsId: string) => {
    setDeletingId(commerceSettingsId);
    try {
      await handleDeleteCommerceSettings(commerceSettingsId);
    } finally {
      setDeletingId(null);
    }
  };

  const columns = commerceSettingsTableColumns;

  const pagination: PaginationConfig = {
    totalItems: totalItems || 0,
    totalPages: totalPages || 0,
    currentPage,
    rowsPerPage: pageSize,
    onPageChange: handlePageChange,
    onRowsPerPageChange: handlePageSizeChange,
  };

  if (error) {
    return (
      <div className='text-center py-8'>
        <p className='text-red-600'>Error al cargar las configuraciones</p>
      </div>
    );
  }

  return (
    <div className='w-full'>
      <Table
        columns={columns}
        data={commerceSettings || []}
        isLoading={isLoading}
        pagination={pagination}
        renderActions={(rowData: CommerceSettings) => (
          <Authorize permission={BUSINESS_ROLE_GROUPS.operations} fallback={null}>
            <div className='flex space-x-2'>
              <Tooltip title='Editar'>
                <IconButton
                  size='small'
                  onClick={() => handleEdit(rowData.id)}
                  className='text-blue-600 hover:text-blue-800'
                >
                  <EditIcon fontSize='small' />
                </IconButton>
              </Tooltip>
              <Tooltip title='Eliminar'>
                <IconButton
                  size='small'
                  onClick={() => handleDelete(rowData.id)}
                  disabled={
                    deletingId === rowData.id || isDeletingCommerceSettings
                  }
                  className='text-red-600 hover:text-red-800 disabled:opacity-50'
                >
                  <DeleteIcon fontSize='small' />
                </IconButton>
              </Tooltip>
            </div>
          </Authorize>
        )}
      />
    </div>
  );
};

export default CommerceSettingsTable;
